<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import { MailingAddressAnswerValue, Province } from "~/api";
import { showError } from "~/utils/handleAPIError";

interface AddressPlaceholders {
  province?: string;
  city?: string;
  streetAddress?: string;
  postalCode?: string;
}
withDefaults(
  defineProps<{
    disabled?: boolean;
    placeholders?: AddressPlaceholders;
  }>(),
  {
    disabled: false,
    placeholders: () => ({
      province: "Select",
      city: "My City",
      streetAddress: "4 - 123 Main St",
      postalCode: "A1A 1A1",
    }),
  },
);

const modelValue = defineModel<Partial<MailingAddressAnswerValue>>();
const localPostalCode = ref<string | undefined>(modelValue.value?.postalCode);

// The postal code field binds to localPostalCode rather than directly to
// modelValue, so sync it when the model changes externally (a failed
// save/delete rollback, or a reload). The equality guard means this never fires
// on the user's own keystrokes, which already update localPostalCode.
watch(
  () => modelValue.value?.postalCode,
  (newVal) => {
    if (newVal !== localPostalCode.value) {
      localPostalCode.value = newVal;
    }
  },
);

const provinceLabels: Record<Province, string> = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland & Labrador",
  NT: "Northwest Territories",
  NS: "Nova Scotia",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

const handleUpdateProvince = (newVal: Province) => {
  if (!provinceLabels[newVal]) {
    showError("Invalid province key: " + newVal);
    return;
  }

  modelValue.value = {
    ...modelValue.value,
    province: newVal,
  };
};

const handleUpdateCity = (newVal: string) => {
  modelValue.value = {
    ...modelValue.value,
    city: newVal,
  };
};

const handleUpdatePostalCode = (newVal: string) => {
  localPostalCode.value = newVal;
  nextTick(() => {
    if (!isPostalCodeInvalid.value) {
      modelValue.value = {
        ...modelValue.value,
        postalCode: newVal,
      };
    }
  });
};

const postalCodeRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;

const validatePostalCode = (postalCode: string | undefined) => {
  postalCode = postalCode?.toUpperCase().replace(/(\w{3})(\w)/, "$1 $2"); //added this here so validation works on pasted postalcode
  if (postalCode) {
    return postalCodeRegex.test(postalCode);
  }
};

const isPostalCodeInvalid = computed(() => {
  return localPostalCode.value && !validatePostalCode(localPostalCode.value);
});

const inPdf = useIsInPdfMode();

const handleUpdateStreetAddress = (newVal: string) => {
  modelValue.value = {
    ...modelValue.value,
    streetAddress: newVal,
  };
};

const formatPostalCode = (val: string) => {
  if (!val) return "";
  // Ensure uppercase and insert a space after the third character
  return val.toUpperCase().replace(/(\w{3})(\w)/, "$1 $2");
};

const parsePostalCode = (val: string) => {
  if (!val) return "";
  // don't remove spaces; formatter takes care of uppercasing
  return val;
};
</script>

<template>
  <div class="address-fields" :class="{ inPdf: inPdf }">
    <el-input
      :placeholder="placeholders.streetAddress"
      :model-value="modelValue?.streetAddress"
      :disabled="disabled"
      @update:model-value="handleUpdateStreetAddress"
    >
      <template #prepend> Street Address</template>
    </el-input>

    <el-input
      :model-value="modelValue?.city"
      :placeholder="placeholders.city"
      :disabled="disabled"
      @update:model-value="handleUpdateCity"
    >
      <template #prepend>City</template>
    </el-input>

    <div v-if="!inPdf" class="province-field">
      <span class="label"> Province </span>
      <el-select
        :model-value="modelValue?.province"
        :placeholder="placeholders.province"
        :disabled="disabled"
        @update:model-value="handleUpdateProvince"
      >
        <el-option
          v-for="province in Object.values(Province)"
          :key="province"
          :label="provinceLabels[province]"
          :value="province"
        />
      </el-select>
    </div>
    <el-input v-else :disabled="disabled">
      <template #prepend>Province</template>
    </el-input>

    <div class="postalCode-field">
      <span v-if="isPostalCodeInvalid" class="invalid-input">
        Invalid Input
      </span>
      <el-input
        :model-value="localPostalCode"
        :placeholder="placeholders.postalCode"
        :maxlength="7"
        :formatter="formatPostalCode"
        :parser="parsePostalCode"
        :disabled="disabled"
        @update:model-value="handleUpdatePostalCode"
      >
        <template #prepend>Postal Code</template>
      </el-input>
    </div>
  </div>
</template>

<style lang="less">
.address-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .el-input__wrapper {
    flex-grow: 0;
  }

  .el-input-group__prepend {
    flex-grow: 1;
    justify-content: right;
  }

  .province-field {
    color: var(--el-color-info);
    background-color: var(--el-fill-color-light);
    display: flex;
    align-items: center;
    padding: 0 0 0 20px;
    border-right: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    justify-content: right;
    .label {
      padding: 0 12px 0 0;
      color: var(--el-font-color, var(--form-input-text-color));
    }

    .el-select {
      --el-select-border-color-hover: var(--el-border-color-hover);
      --el-select-disabled-color: var(--el-disabled-text-color);
      --el-select-disabled-border: var(--el-disabled-border-color);
      --el-select-font-size: var(--el-font-size-base);
      --el-select-close-hover-color: var(--el-text-color-secondary);
      --el-select-input-color: var(--el-text-color-placeholder);
      --el-select-multiple-input-color: var(--el-text-color-regular);
      --el-select-input-focus-border-color: var(--el-color-primary);
      --el-select-input-font-size: 14px;
      width: calc(
        var(--standard-input-width) + 22px
      ); // plus the padding other inputs have

      .el-select__wrapper.is-disabled,
      .el-select__wrapper.is-disabled.is-hovering {
        box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
        background-color: #f2f2f2;
        .el-select__placeholder {
          color: #444142;
        }
      }
    }
    .el-select__wrapper {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      text-align: right;

      &.is-disabled,
      &.is-disabled.is-hovering {
        box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      }
    }

    .el-select__wrapper .el-select__placeholder.is-transparent {
      text-align: right;
    }
  }
  .postalCode-field {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .invalid-input {
      color: var(--el-color-error);
    }
  }
  .el-input.is-disabled {
    .el-input__wrapper {
      background-color: #f2f2f2;
    }
    .el-input__inner {
      -webkit-text-fill-color: #444142;
    }
  }

  &.inPdf {
    .el-input-group__append,
    .el-input-group__prepend {
      background-color: transparent;
      font-size: 10pt;
    }
  }
}
</style>
